// File: src/components/navigation/staff-sidebar.tsx
// Purpose: Staff portal sidebar navigation with dark mode support
// InstaDev - Staff Sidebar


import { Link } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import {
  LayoutDashboard,
  Clock,
  CheckSquare,
  FolderOpen,
  User,
  Calendar,
  FileText,
  Settings,
  LogOut,
  Home,
  ChevronRight,
  Bot,
  BarChart3,
  MessageSquare,
  Ticket,
  Kanban,
  ListChecks,
  ChevronDown
} from 'lucide-react'

interface NavItem {
  title: string
  href: string
  icon: any
  description?: string
  subItems?: NavItem[]
}

const staffNavItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/staff/dashboard',
    icon: LayoutDashboard,
    description: 'Overview and metrics'
  },
  {
    title: 'AI Assistant',
    href: '/staff/ai-chat',
    icon: Bot,
    description: 'AI-powered help'
  },
  {
    title: 'Chat System',
    href: '/staff/chat-system',
    icon: MessageSquare,
    description: 'Team communication'
  },
  {
    title: 'My Tasks',
    href: '/staff/tasks',
    icon: CheckSquare,
    description: 'Assigned tasks'
  },
  {
    title: 'Timesheets',
    href: '/staff/timesheets',
    icon: Clock,
    description: 'Log your hours'
  },
  {
    title: 'Projects',
    href: '/staff/projects',
    icon: FolderOpen,
    description: 'Your projects'
  },
  {
    title: 'Tickets',
    href: '/staff/tickets',
    icon: Ticket,
    description: 'Task management',
    subItems: [
      {
        title: 'Board',
        href: '/staff/board',
        icon: Kanban,
        description: 'Kanban board'
      },
      {
        title: 'Sprints',
        href: '/staff/sprints',
        icon: ListChecks,
        description: 'Sprint management'
      },
      
    ]
  },
  {
    title: 'Calendar',
    href: '/staff/calendar',
    icon: Calendar,
    description: 'Schedule & events'
  },
  {
    title: 'Documents',
    href: '/staff/documents',
    icon: FileText,
    description: 'Shared files'
  },
 
  {
    title: 'Profile',
    href: '/staff/profile',
    icon: User,
    description: 'Your profile'
  },
  {
    title: 'Avatar Analysis',
    href: '/staff/avatar-analysis',
    icon: BarChart3,
    description: 'Skills & growth analysis'
  }
]

interface StaffSidebarProps {
  isCollapsed?: boolean
}

export default function StaffSidebar({ isCollapsed = false }: StaffSidebarProps) {
  const location = useLocation()
  const pathname = location.pathname
  const [expandedSections, setExpandedSections] = useState<string[]>(['Tickets'])

  const toggleSection = (title: string) => {
    setExpandedSections(prev =>
      prev.includes(title) ? [] : [title]
    )
  }

  return (
    <nav className="flex-1 overflow-y-auto py-4">
      {/* Main Navigation */}
      <div className="px-3 space-y-1">
        {staffNavItems.map((item) => {
          const Icon = item.icon
          const isActive = pathname === item.href
          const hasSubItems = item.subItems && item.subItems.length > 0
          const isExpanded = expandedSections.includes(item.title)
          const isSubItemActive = hasSubItems && item.subItems?.some(sub => pathname === sub.href)

          return (
            <div key={item.href}>
              {hasSubItems ? (
                <>
                  <button
                    onClick={() => toggleSection(item.title)}
                    className={cn(
                      "w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all",
                      isSubItemActive
                        ? "bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-400"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white",
                      isCollapsed && "justify-center px-2"
                    )}
                  >
                    <Icon className={cn(
                      "flex-shrink-0",
                      isSubItemActive ? "text-green-600 dark:text-green-400" : "text-gray-400 dark:text-gray-500",
                      isCollapsed ? "h-5 w-5" : "h-4 w-4"
                    )} />
                    {!isCollapsed && (
                      <>
                        <div className="flex-1 text-left">
                          <div>{item.title}</div>
                          {item.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {item.description}
                            </div>
                          )}
                        </div>
                        <ChevronDown className={cn(
                          "h-4 w-4 transition-transform",
                          isExpanded ? "rotate-180" : ""
                        )} />
                      </>
                    )}
                  </button>
                  {!isCollapsed && isExpanded && (
                    <div className="ml-7 mt-1 space-y-1">
                      {item.subItems?.map((subItem) => {
                        const SubIcon = subItem.icon
                        const isSubActive = pathname === subItem.href
                        return (
                          <Link
                            key={subItem.href}
                            to={subItem.href}
                            className={cn(
                              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all",
                              isSubActive
                                ? "bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-400"
                                : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                            )}
                          >
                            <SubIcon className={cn(
                              "flex-shrink-0 h-3.5 w-3.5",
                              isSubActive ? "text-green-600 dark:text-green-400" : "text-gray-400 dark:text-gray-500"
                            )} />
                            <div className="flex-1">
                              <div className="text-xs">{subItem.title}</div>
                            </div>
                            {isSubActive && (
                              <ChevronRight className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                            )}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all",
                    isActive
                      ? "bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-400"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white",
                    isCollapsed && "justify-center px-2"
                  )}
                  title={isCollapsed ? item.title : undefined}
                >
                  <Icon className={cn(
                    "flex-shrink-0",
                    isActive ? "text-green-600 dark:text-green-400" : "text-gray-400 dark:text-gray-500",
                    isCollapsed ? "h-5 w-5" : "h-4 w-4"
                  )} />
                  {!isCollapsed && (
                    <>
                      <div className="flex-1">
                        <div>{item.title}</div>
                        {item.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {item.description}
                          </div>
                        )}
                      </div>
                      {isActive && (
                        <ChevronRight className="h-4 w-4 text-green-600 dark:text-green-400" />
                      )}
                    </>
                  )}
                </Link>
              )}
            </div>
          )
        })}
      </div>

      {/* Settings Section */}
      {!isCollapsed && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          {/* <div className="px-3 space-y-1">
            <Link
              href="/staff/settings"
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all",
                pathname === '/staff/settings'
                  ? "bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-400"
                  : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
              )}
            >
              <Settings className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span>Settings</span>
            </Link>
          </div> */}
        </div>
      )}
    </nav>
  )
}